import type { FormFieldFieldHelper } from '@ingka/form-field';
import FormField from '@ingka/form-field';
import InputField from '@ingka/input-field';
import DOMPurify from 'dompurify';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';
import type { Control, FieldError, FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shouldValidate } from '../../utils';
import { getPlaceholder, toIsoDate, toLocalizedDate } from './util';

export type ControlledDateInputProps<TFormData extends FieldValues = FieldValues> = {
  labelKey: string;
  control: Control<TFormData>;
  name: Path<TFormData>;
  locale: string; // Eg sv-SE or en-US
  disabled?: boolean;
  fieldHelper?: FormFieldFieldHelper;
  suffix?: string;
};

/**
 * Locale-aware date input
 *
 * Uses internal state for controlling the text input since the control wants
 * it in ISO format.
 */
export const ControlledDateInput = <TFormData extends FieldValues = FieldValues>({
  control,
  labelKey,
  name,
  locale,
  disabled = false,
  fieldHelper,
  suffix,
}: ControlledDateInputProps<TFormData>) => {
  const { t } = useTranslation();

  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({ name, control });

  const [internalValue, setInternalValue] = useState<{ iso: string; input: string }>({
    iso: value ?? '',
    input: '',
  });

  useEffect(() => {
    // Set the initial internal value, and reset it if locale changes
    setInternalValue(({ iso, input: localized }) => ({
      iso,
      input: toLocalizedDate(iso, locale) ?? localized,
    }));
  }, [locale]);

  // Translated format for use as placeholder, eg 'ÅÅÅÅ-MM-DD' for Swedish
  const translatedFormat = getPlaceholder(locale);

  const transformErrorMessage = (error: FieldError | undefined) => {
    return error?.message ? t(error.message, { format: translatedFormat }) : '';
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const iso = toIsoDate(value, locale) ?? '';
    setInternalValue({ iso, input: value });
    // Send date transformed to ISO (if possible) to the parent form control
    onChange(iso);
  };

  const handleOnBlur = () => {
    // Try to auto-correct by formatting according to our config
    const { iso } = internalValue;
    if (iso) {
      const input = toLocalizedDate(iso, locale);
      if (input) {
        setInternalValue({ iso, input });
      }
    }
    onBlur();
  };

  return (
    <FormField
      shouldValidate={shouldValidate(error)}
      valid={!error}
      validation={{
        msg: transformErrorMessage(error),
        type: 'error',
      }}
      fieldHelper={{ msg: translatedFormat, ...fieldHelper }}
    >
      <InputField
        id={`lottery-form-${name}`}
        label={`${t(labelKey)}`}
        type="text"
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        disabled={disabled}
        suffixLabel={suffix}
        value={DOMPurify.sanitize(internalValue.input)}
      />
    </FormField>
  );
};
